
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {createFeature} from "../Feature.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {loadRoiPoints, DEFAULT_POINT_BUDGET} from "../volume/RoiPointLoader.js";
import {hiddenClassCodes} from "../volume/classFilter.js";
import {startVolumeJob, cancelActiveVolumeJob, getActiveJobProgress, hasActiveVolumeJob} from "../volume/VolumeJob.js";
import {showVolumeErrorPanel, buildVolumeLabelLines} from "../volume/VolumeResultPanel.js";
import {buildVolumeQaVisual, DEFAULT_QA_VISIBILITY} from "../volume/VolumeQaVisual.js";
import {nextVolumeJobId} from "../volume/volumeJobId.js";
import {buildVolumeSnapshot, isVolumeSnapshot, snapshotMatchesInput, volumeResultFromSnapshot} from "../volume/volumeSnapshot.js";
import {createRecomputeScheduler} from "../volume/recomputeScheduler.js";
import {showToast} from "../CopyFeedback.js";

import {t, T} from "../i18n/index.js";
// "Areal og volumen" (Fase 6) - digitizes a closed polygon along the
// physical rim/boundary of an area, then computes fill/cut volume between
// that boundary's own best-fit reference plane and the observed point-
// cloud surface inside it (see src/digitization/geometry/volume/ - the
// Fase 3-5.5 measurement core this tool is a thin capture/orchestration
// wrapper around; NOTHING in that core is touched or reimplemented here).
//
// Custom capture (not the generic vertex-capture flow, which has no hook
// for an async step before feature creation - see capturePolygon() below):
// digitize a closed polygon (own preview loop, mirroring _captureVector()'s
// shape but for a closed Polygon visual) -> validate -> deterministic ROI
// point extraction from the octree (RoiPointLoader.js) -> Web Worker
// computation (VolumeJob.js/volumeWorker.js) -> on success, commit exactly
// one Feature (the digitized polygon, with the computed result attached)
// through the SAME `featureManager.add()`/`geometry_changed` sync every
// other tool uses - never a parallel commit path. On any failure (ROI
// load error, Worker error, cancellation), no feature is created.
export const VolumeTool = {
	id: "digitize_volume",
	name: T("Mål areal og volumen"),
	category: "specialized",
	kind: "measurement",
	toolbarGroup: "measurement",
	toolbarOrder: 40,
	icon: "volume_area.svg",
	resultGeometryType: "Polygon",
	featureType: T("Areal og volumen"),

	tooltip: T("Beregn areal, påfyldning og udgravning for et digitaliseret område ud fra punktskyens overflade."),

	customCapture(dm, tool){
		runVolumeCapture(dm, tool);
	},

	// Restore extension point (see DigitizationModule._buildLoadedFeature): the stored result snapshot (feature.domainData.volume) is
	// shown as it was saved - result label, panel values - with NO ROI load and NO computation. The surface/QA visual is runtime-only
	// and is not restored; it exists again after the next computation. The restored object is wired exactly like a new one, so editing
	// its polygon runs the normal recomputation. A snapshot that no longer describes the polygon (edited before its recomputation
	// finished, or an older algorithm version) is shown as outdated; nothing is recomputed automatically.
	decorateLoaded(visual, feature, dm){
		const snapshot = feature.domainData && feature.domainData.volume;
		if(isVolumeSnapshot(snapshot)){
			const result = volumeResultFromSnapshot(snapshot);
			const stale = !snapshotMatchesInput(snapshot, feature.geometry.coordinates);
			feature.properties.attributes = {
				...(feature.properties.attributes || {}),
				volumeResult: result,
				stale,
				qaVisibility: {...DEFAULT_QA_VISIBILITY},
			};
			visual.setResultLabel(volumeLabelLines(result, {stale, surface: false}), result.plane.origin);
			visual.setVertexLabelsSuppressed(true);
		}
		wireVolumeFeature(dm, VolumeTool, feature, visual);
	},
};

DigitizationTools.register(VolumeTool);

// Task section 14: concrete, per-stage progress text (replacing the one
// broad "Analyserer overfladen..." that used to sit unexplained for however
// long the whole rest of the pipeline took). Convergence has no stage here
// any more - it is not part of the default compute flow (section 8-10).
const PROGRESS_TEXT = {
	referencePlane: T("Beregner referenceplan…"),
	filter: T("Udvælger punkter…"),
	spacing: T("Analyserer punkttæthed…"),
	reduction: T("Reducerer overflade…"),
	triangulation: T("Triangulerer overflade…"),
	coverage: T("Vurderer datadækning…"),
	integration: T("Beregner volumen…"),
};

// Watchdog (task section 12): not a hard timeout (ROI download time can
// legitimately vary a lot with network/data size) - just a visibility
// guarantee that the UI never again sits on a stage name indefinitely
// without at least telling the user (and the console) that something is
// taking longer than expected.
const WATCHDOG_STALL_MS = 15000;
const WATCHDOG_POLL_MS = 2000;

// Closed-polygon capture loop - left-click adds a vertex (previewed live,
// closed), right-click finishes (requires >= 3 vertices), ESC/cancel_insertions
// discards everything. Mirrors DigitizationModule._captureVector()'s own
// shape (same cleanup/onCancel/onMouseUp structure every capture flow in
// this codebase uses) but builds a closed "Polygon" FeatureVisual instead
// of an open helper LineString, for a properly closed live preview.
function capturePolygon(dm, tool, onDone){
	const domElement = dm.viewer.renderer.domElement;
	const visual = new FeatureVisual("Polygon");
	dm.scene.add(visual);

	let finished = false;

	const cleanup = () => {
		domElement.removeEventListener("mouseup", onMouseUp, false);
		dm.viewer.removeEventListener("cancel_insertions", onCancel);
		dm.clearRubberBand();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		dm.scene.remove(visual);
		onDone(null);
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			const location = dm._pickPlacementPosition(e);
			if(!location) return;
			visual.appendAtEnd(location);
			dm._markOperationStarted();
			dm.syncRubberBandToVisual(visual);
			dm._showInstruction(tool.name, t("Venstreklik for at tilføje flere punkter. Højreklik for at afslutte (mindst 3 punkter, nu {0}). ESC annullerer.", visual.points.length));
			return;
		}

		if(e.button === THREE.MOUSE.RIGHT){
			if(visual.points.length < 3) return; // not enough yet - ignore, keep capturing
			finished = true;
			cleanup();
			onDone(visual);
		}
	};

	domElement.addEventListener("mouseup", onMouseUp, false);
	dm.viewer.addEventListener("cancel_insertions", onCancel);

	dm._showInstruction(tool.name, t("Digitalisér polygonen langs den fysiske rand omkring det område, der skal beregnes. Venstreklik tilføjer punkter, højreklik afslutter (mindst 3 punkter). ESC annullerer."));
}

// The result label of a measurement (the result surface PointView actually shows): the three values, plus one note line when the result
// needs one - outdated (the polygon changed and the new calculation has not finished, or the stored result describes another polygon),
// or restored without its surface (the surface is runtime-only and is shown again after the next calculation).
export function volumeLabelLines(result, {stale = false, surface = true} = {}){
	const lines = buildVolumeLabelLines(result);
	if(stale) lines.push(t("⚠ Forældet resultat"));
	else if(!surface) lines.push(t("Overfladen vises ikke (den gemmes ikke)"));
	return lines;
}

// The point cloud a measurement belongs to: the one whose name is the model id (feature.pointcloudId). Without a model id (no active
// model when the tool ran) the first loaded cloud is used, as before.
export function resolvePointcloud(dm, pointcloudId){
	const clouds = dm.viewer.scene.pointclouds;
	if(pointcloudId == null) return clouds[0] || null;
	return clouds.find((cloud) => cloud.name === pointcloudId) || null;
}

// The wiring every Areal og volumen object gets, new or restored: a geometry change marks the result outdated and (after the drag
// ended / a short quiet period) starts the one existing recomputation.
function wireVolumeFeature(dm, tool, feature, visual){
	const recomputer = createVolumeRecomputer(dm, tool, feature.id, visual);
	visual.addEventListener("geometry_changed", () => {
		const current = dm.featureManager.get(feature.id);
		if(!current) return;
		const result = current.properties.attributes && current.properties.attributes.volumeResult;
		// the first change after a valid result: the label says so (not on every move of a drag)
		if(result && !current.properties.attributes.stale) visual.setResultLabel(volumeLabelLines(result, {stale: true}), result.plane.origin);
		dm.featureManager.updateProperties(feature.id, {
			attributes: {...current.properties.attributes, stale: true},
		});
		if(!visual.isDragging) recomputer.schedule(RECOMPUTE_QUIET_MS);
	});
	visual.addEventListener("vertex_drop", () => recomputer.schedule(0));
}

// Ro-periode (ms) efter en afsluttet, ikke-træk geometriændring, før der genberegnes. Et vertex-slip genberegner med det samme.
const RECOMPUTE_QUIET_MS = 300;

// Genberegner Mål areal og volumen for en EKSISTERENDE registrering, når polygonen er ændret (brugeren har flyttet et vertex
// og sluppet det). Samme kæde som den første beregning (RoiPointLoader -> VolumeJob/Worker -> label + QA-visualisering), men:
//   - registreringen bevares (id, navn, mappe, QA-valg) - kun volumeResult/label/QA-visual udskiftes, stale -> false
//   - fejl er ikke-modale (toast, resultatet bliver stående som forældet) - registreringen fjernes aldrig
//   - en ny ændring undervejs afløser den igangværende beregning (token), så kun den seneste geometri vinder
// En ændring der ikke har gjort resultatet forældet (stale=false), genberegnes ikke.
function createVolumeRecomputer(dm, tool, featureId, visual){
	let token = 0;
	let ownJobId = null; // job-id for denne registrerings egen (seneste) genberegning
	let scheduler = null;

	const toastError = (message) => showToast(t("Genberegning af areal og volumen mislykkedes: {0}", message), 5000);

	const run = async () => {
		const feature = dm.featureManager.get(featureId);
		if(!feature || !dm.visuals.get(featureId)) return; // registreringen er slettet
		const attributes = feature.properties.attributes || {};
		if(!attributes.stale) return; // intet at genberegne (fx et klik på punktet uden at flytte det)
		if(visual.isDragging) return; // afventer 'vertex_drop'

		const pointcloud = resolvePointcloud(dm, feature.pointcloudId);
		if(!pointcloud){
			toastError(t("Ingen punktsky er indlæst."));
			return;
		}
		const polygonWorld = visual.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));
		if(polygonWorld.length < 3) return;

		// en ANDEN måling (fx en ny Mål areal og volumen-tegning) kører: lad den være og prøv igen lidt senere
		if(hasActiveVolumeJob()){
			const progress = getActiveJobProgress();
			if(!progress || progress.jobId !== ownJobId){
				scheduler.schedule(1000);
				return;
			}
		}

		const mine = ++token;
		const jobId = nextVolumeJobId();
		ownJobId = jobId;
		cancelActiveVolumeJob(); // vores egen forældede beregning afløses (dens svar ignoreres via token)
		showToast(t("Genberegner areal og volumen…"), 120000);

		try{
			// every calculation measures what is switched on when it runs
			const roi = await loadRoiPoints(pointcloud, polygonWorld, {jobId, pointBudget: DEFAULT_POINT_BUDGET, hiddenClasses: hiddenClassCodes(dm.viewer.classifications)});
			if(mine !== token) return;
			if(roi.rawPointCount === 0){
				toastError(t("Ingen punktskydata inden for polygonen."));
				return;
			}

			const polygonFlat = new Float64Array(polygonWorld.length * 3);
			polygonWorld.forEach((p, i) => { polygonFlat[i * 3] = p.x; polygonFlat[i * 3 + 1] = p.y; polygonFlat[i * 3 + 2] = p.z; });

			const outcome = await new Promise((resolve) => {
				startVolumeJob(roi.positions, polygonFlat, {
					jobId,
					onProgress: () => {},
					onResult: (msg) => resolve({ok: true, msg}),
					onError: (message, technical) => resolve({ok: false, message, technical}),
				});
			});
			if(mine !== token) return; // ændret igen undervejs - kun den seneste geometri må gemmes

			if(!outcome.ok){
				toastError(outcome.message);
				return;
			}

			const current = dm.featureManager.get(featureId);
			if(!current || !dm.visuals.get(featureId)) return;

			const result = outcome.msg.result;
			result.timings.roiLoad = roi.ms;
			result.roiNodesLoaded = roi.nodesLoaded;
			result.roiFrontierLevel = roi.frontierLevel;
			result.roiPointBudget = roi.pointBudget;
			result.roiNodePointCount = roi.nodePointCount;
			result.roiBboxFilteredPointCount = roi.bboxFilteredPointCount;
			result.excludedClasses = roi.excludedClasses;

			// QA-visualiseringen genopbygges til den nye overflade; brugerens valg af synlige dele bevares
			const qaVisibility = (current.properties.attributes && current.properties.attributes.qaVisibility) || {...DEFAULT_QA_VISIBILITY};
			try{
				const qaBuild = buildVolumeQaVisual(result, outcome.msg.vizSupported, outcome.msg.vizUnsupported, result.boundaryLocal, qaVisibility, visual);
				const oldGroup = visual.volumeQaGroup;
				if(oldGroup){
					visual.remove(oldGroup);
					oldGroup.traverse((o) => {
						if(o.geometry && o.geometry.dispose) o.geometry.dispose();
						if(o.material){
							for(const m of (Array.isArray(o.material) ? o.material : [o.material])) if(m.dispose) m.dispose();
						}
					});
				}
				visual.add(qaBuild.group);
				visual.volumeQaGroup = qaBuild.group;
				result.measurementTriangleCount = result.reconstructedFaceCount;
				result.visualTriangleCount = qaBuild.visualTriangleCount;
				result.qaMeshCount = qaBuild.meshCount;
				result.qaBuildTimeMs = qaBuild.buildTimeMs;
				result.qaDebug = qaBuild.debug;
			}catch(e){
				console.warn("Mål areal og volumen: QA-visualisering fejlede ved genberegning", e);
			}

			visual.setResultLabel(buildVolumeLabelLines(result), result.plane.origin);
			// the stored snapshot belongs to the polygon this computation used; if the polygon has been edited since, the result stays outdated
			const snapshot = buildVolumeSnapshot(result, polygonWorld);
			dm.featureManager.setDomainData(featureId, "volume", snapshot);
			dm.featureManager.updateProperties(featureId, {
				attributes: {...current.properties.attributes, volumeResult: result, stale: !snapshotMatchesInput(snapshot, current.geometry.coordinates)},
			});
			showToast(t("Areal og volumen genberegnet"), 1600);
		}catch(err){
			if(mine !== token) return;
			toastError((err && err.message) || String(err));
		}
	};

	scheduler = createRecomputeScheduler({run, delayMs: RECOMPUTE_QUIET_MS});
	return {schedule: (delay) => scheduler.schedule(delay)};
}

async function runVolumeCapture(dm, tool){
	const visual = await new Promise((resolve) => capturePolygon(dm, tool, resolve));
	if(!visual){
		dm._hideToolHint();
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	const polygonWorld = visual.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));

	let cancelled = false;
	const onCancelDuringWork = () => { cancelled = true; cancelActiveVolumeJob(); };
	dm.viewer.addEventListener("cancel_insertions", onCancelDuringWork);

	const abortToVisualRemoved = () => {
		dm.viewer.removeEventListener("cancel_insertions", onCancelDuringWork);
		dm.scene.remove(visual);
		dm._hideToolHint();
		if(dm.activeTool === tool) dm.stopInsertion();
	};

	// the model this measurement is made on is fixed now, not when the (asynchronous) computation has finished
	const pointcloudId = dm._pointcloudIdForTool(tool.id);
	const pointcloud = resolvePointcloud(dm, pointcloudId);
	if(!pointcloud){
		abortToVisualRemoved();
		showVolumeErrorPanel(t("Ingen punktsky er indlæst."), undefined, dm);
		return;
	}

	// One shared, human-readable id spans BOTH the ROI-load phase and the
	// Worker-compute phase (task section 2) - every console line for this
	// one measurement attempt can be found under "[Volume job N]".
	const jobId = nextVolumeJobId();
	const approxXYArea = Math.abs(polygonWorld.reduce((sum, p, i) => {
		const q = polygonWorld[(i + 1) % polygonWorld.length];
		return sum + (p.x * q.y - q.x * p.y);
	}, 0) / 2);
	console.log(`[Volume job ${jobId}] polygon vertices=${polygonWorld.length}, approx. XY area=${approxXYArea.toFixed(2)} m² (exact referenceArea reported later, in the fitted plane)`);

	dm._showInstruction(tool.name, t("Indlæser punktskydata…"));
	let roi;
	try{
		roi = await loadRoiPoints(pointcloud, polygonWorld, {jobId, pointBudget: DEFAULT_POINT_BUDGET, hiddenClasses: hiddenClassCodes(dm.viewer.classifications)});
	}catch(err){
		abortToVisualRemoved();
		if(!cancelled) showVolumeErrorPanel(t("Kunne ikke indlæse punktskydata: {0}", (err && err.message) || err), undefined, dm);
		return;
	}
	if(cancelled){ abortToVisualRemoved(); return; }
	if(roi.rawPointCount === 0){
		abortToVisualRemoved();
		showVolumeErrorPanel(t("Ingen punktskydata blev fundet inden for polygonen."), undefined, dm);
		return;
	}

	dm._showInstruction(tool.name, t("Beregner areal og volumen…"));
	const polygonFlat = new Float64Array(polygonWorld.length * 3);
	polygonWorld.forEach((p, i) => { polygonFlat[i * 3] = p.x; polygonFlat[i * 3 + 1] = p.y; polygonFlat[i * 3 + 2] = p.z; });

	// Watchdog (task section 12): never a hard timeout (ROI size/network
	// speed legitimately vary), but the UI must never again just sit on a
	// stage name forever without at least saying something is taking
	// longer than expected, and logging a console warning for diagnosis.
	let watchdogWarned = false;
	const watchdogTimer = setInterval(() => {
		const progress = getActiveJobProgress();
		if(!progress || progress.jobId !== jobId) return;
		const quietMs = Date.now() - progress.lastProgressAt;
		if(quietMs > WATCHDOG_STALL_MS){
			if(!watchdogWarned){
				watchdogWarned = true;
				console.warn(`[Volume job ${jobId}] WATCHDOG: no progress for ${quietMs} ms (last stage: ${progress.lastStage})`);
			}
			const stageText = PROGRESS_TEXT[progress.lastStage] ? t(PROGRESS_TEXT[progress.lastStage]) : progress.lastStage;
			dm._showInstruction(tool.name, t("Beregningen tager længere tid end forventet – trin: {0} (ESC annullerer)", stageText));
		}
	}, WATCHDOG_POLL_MS);

	const outcome = await new Promise((resolve) => {
		startVolumeJob(roi.positions, polygonFlat, {
			jobId,
			onProgress: (stage) => { watchdogWarned = false; dm._showInstruction(tool.name, PROGRESS_TEXT[stage] ? t(PROGRESS_TEXT[stage]) : t("Beregner…")); },
			onResult: (msg) => resolve({ok: true, msg}),
			onError: (message, technical) => resolve({ok: false, message, technical}),
		});
	});
	clearInterval(watchdogTimer);

	dm.viewer.removeEventListener("cancel_insertions", onCancelDuringWork);
	dm._hideToolHint();

	if(cancelled){
		dm.scene.remove(visual);
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	if(!outcome.ok){
		dm.scene.remove(visual);
		showVolumeErrorPanel(t("Beregningen mislykkedes: {0}", outcome.message), outcome.technical, dm);
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	const result = outcome.msg.result;
	result.timings.roiLoad = roi.ms;
	result.roiNodesLoaded = roi.nodesLoaded;
	result.roiFrontierLevel = roi.frontierLevel;
	result.roiPointBudget = roi.pointBudget;
	result.roiNodePointCount = roi.nodePointCount;
	result.roiBboxFilteredPointCount = roi.bboxFilteredPointCount;
	result.excludedClasses = roi.excludedClasses;

	const feature = createFeature({
		geometryType: "Polygon",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		attributes: {
			volumeResult: result,
			stale: false,
			qaVisibility: {...DEFAULT_QA_VISIBILITY},
		},
		domainData: {volume: buildVolumeSnapshot(result, polygonWorld)},
		pointcloudId,
	});

	visual.setInfo(feature.properties.name, feature.properties.description);

	// Compact result label (task section 2-5 of the UI-simplification
	// iteration) - the primary on-screen result, replacing the large
	// technical dialog in the normal flow. Anchored at the reference
	// plane's own already-computed, length-weighted boundary centroid
	// (result.plane.origin, world-space) - a robust anchor already
	// computed by the measurement core, not a new geometry calculation and
	// not tied to any one polygon vertex (task section 4).
	visual.setResultLabel(buildVolumeLabelLines(result), result.plane.origin);
	// Individual polygon-vertex coordinate labels (X/Y/Z at every corner)
	// would otherwise compete visually with the result label and the TIN -
	// hidden after a successful computation (task section 6/7). The
	// polygon outline/vertices themselves stay exactly as visible/editable
	// as before; only their coordinate labels are suppressed.
	visual.setVertexLabelsSuppressed(true);

	dm._wireFeatureLifecycle(feature, visual); // visuals map, FeatureManager, snap targets, geometry_changed sync, feature_clicked

	// tool-specific: a stored volume result must never silently keep looking
	// valid after the polygon it was computed from changes - mark it stale
	// (no automatic recompute in this evaluation version)
	// Dynamisk genberegning: resultatet markeres stadig forældet med det samme, men starter en genberegning, når punktet
	// slippes ('vertex_drop'), og for andre, afsluttede geometriændringer (menu-handlinger) efter en kort ro-periode.
	// Under selve trækket (isDragging) regnes der ikke - hver musebevægelse ville ellers starte en ny beregning.
	wireVolumeFeature(dm, tool, feature, visual);

	try{
		// `visual` is passed as the intended parent (task section 6/7) so the
		// QA group's own local matrix is computed correctly relative to
		// whatever `visual`'s actual matrixWorld is - never assumed identity.
		const qaBuild = buildVolumeQaVisual(result, outcome.msg.vizSupported, outcome.msg.vizUnsupported, result.boundaryLocal, DEFAULT_QA_VISIBILITY, visual);
		visual.add(qaBuild.group); // moves/hides/deletes together with the feature's own visual
		// kept on the visual (not the result/feature) so later UI (FeaturePanel's
		// QA visibility checkboxes) can find and toggle it without ever touching
		// the stored measurement - see VolumeQaVisual.js's own header comment
		// (task section 2/11: visualisation state must never affect the result)
		visual.volumeQaGroup = qaBuild.group;
		// task section 6 of the FULL-3D task: coverage/supported-triangle
		// counts no longer exist in the new model - reconstructedFaceCount
		// (pipeline3D.js's own diagnostic) is the closest meaningful analog
		// for this dev-only technical QA field.
		result.measurementTriangleCount = result.reconstructedFaceCount;
		result.visualTriangleCount = qaBuild.visualTriangleCount;
		result.qaMeshCount = qaBuild.meshCount;
		result.qaBuildTimeMs = qaBuild.buildTimeMs;
		result.qaDebug = qaBuild.debug;
	}catch(e){
		// QA visualisation is best-effort only - never let it block a
		// successful, already-committed measurement
		console.warn("Mål areal og volumen: QA-visualisering fejlede", e);
	}

	dm.dispatchEvent({type: "feature_created", feature, visual});

	// task section 2/13 of the UI-simplification iteration: the large
	// technical result dialog (VolumeResultPanel.js's showVolumeResultPanel)
	// is no longer part of the normal success flow - the compact 3D result
	// label above + Egenskaber's simplified summary are the product's
	// actual result surface now. The dialog module itself is untouched and
	// still used for errors (showVolumeErrorPanel, see above) and remains
	// available for future dev/QA use; no data is deleted, only what is
	// shown automatically after a normal, successful computation.

	if(dm.activeTool === tool) dm.stopInsertion();
}
